import React from 'react';
import { CheckCircle, Award, Clock, AlertCircle, Home, RefreshCw } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../../../../../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../../../../components/ui/alert';
import { Badge } from '../../../../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../../../../components/ui/avatar';
import { Separator } from '../../../../../components/ui/separator';
import { useNavigate } from 'react-router-dom';
import { useAuthProviderV2, useRoutesProvider } from '../../../../../providers/provider-hooks';
import { isPathAllowedForRole } from '../../../../auth/utils/is-path-allowed';
import { esteActivitatePracticaCuClase } from '../../../utils/clase-flag';
import { avertizareClaseDejaPunctate, textAvertizareClase } from '../../../utils/clase-avertizare';
import type { LessonDTO, AnswerDTO, LessonAnswerDTO, LessonTypeConfig } from '../../../core/types/lesson.types';
import { LessonAttemptState, LessonType } from '../../../core/types/lesson.types';
import { formatUnixTimestamp } from '../../../../../shared/utils/formatting';

interface CompletionScreenProps {
  lesson: LessonDTO;
  config: LessonTypeConfig;
  slideAnswers: AnswerDTO[];
  lessonAnswer?: LessonAnswerDTO;
  totalPoints?: number;
  /** Clasele pe care serverul le-a raportat ca deja punctate, la 201 (SP-05a). */
  claseDejaPunctate?: string[];
  /**
   * A trimis omul CHIAR ACUM, in sesiunea asta?
   *
   * Ecranul se randeaza si la reintrarea pe o lectie deja completata; avertizarea
   * e despre trimiterea curenta, deci fara flagul asta ar vorbi saptamana viitoare
   * despre ceva ce omul nu mai poate schimba.
   */
  aTrimisAcum?: boolean;
  isPracticeMode?: boolean;  // ✅ New flag for practice mode
}

export const CompletionScreen: React.FC<CompletionScreenProps> = ({
  lesson,
  config,
  slideAnswers,
  lessonAnswer,
  totalPoints = 0,
  isPracticeMode = false,  // ✅ Default to false
  claseDejaPunctate,
  aTrimisAcum = false,
}) => {
  const avertizareClase = avertizareClaseDejaPunctate(claseDejaPunctate, aTrimisAcum);
  const navigate = useNavigate();
  const { userInfo } = useAuthProviderV2();
  const { PATHS, PAGES, getUserHomePage, NAV_LINKS_V2 } = useRoutesProvider();

  // Fiecare tip de lectie are propria pagina de listare, cu propriul `ALLOWED_ROLES` (vezi
  // `routes-provider.tsx`). `/lectii` (LESSON_QUIZ_LISTING) e doar pentru DBEST/ADMIN — un
  // `navigate(PATHS[PAGES.LESSON_QUIZ_LISTING])` necondiționat trimitea un ELEV/PROFESOR care
  // tocmai a terminat o TEMA/PROVOCARE/etc. spre o pagina interzisa lui, exact greseala pe care
  // `navigate('/')` o facea in alta parte (vezi finding cross-plan din review-ul final).
  const lessonTypeListingPage: Record<LessonType, keyof typeof PAGES> = {
    [LessonType.LESSON]: 'LESSON_QUIZ_LISTING',
    [LessonType.HOMEWORK]: 'HOMEWORK_LISTING',
    [LessonType.PRACTICAL_ACTIVITY]: 'PRACTICAL_ACTIVITY_LISTING',
    [LessonType.MONTHLY_CHALLENGE]: 'MONTHLY_CHALLENGES_LISTING',
    [LessonType.CLASS_EXERCISE]: 'CLASS_EXERCISE_LISTING',
    [LessonType.SCHOOL_CHALLENGE]: 'SCHOOL_CHALLENGE_LISTING',
    // DAILY_CHALLENGE nu are propria pagina de listare (ruta e comentata in routes-provider.tsx)
    // — cea mai apropiata e lista de teme.
    [LessonType.DAILY_CHALLENGE]: 'HOMEWORK_LISTING',
  };
  // Navigheaza spre listarea tipului de lectie DOAR daca rolul curent chiar are voie acolo;
  // altfel, ca sa nu repete greseala `navigate('/')`, cade pe home-ul rolului (acelasi resolver
  // folosit si de restul redirecturilor).
  const listingPath = PATHS[PAGES[lessonTypeListingPage[config.type]]];
  const lessonsListingTarget = isPathAllowedForRole(listingPath, userInfo?.role, NAV_LINKS_V2)
    ? listingPath
    : getUserHomePage(userInfo?.role);

  const answeredCount = slideAnswers.length;
  const totalSlides = lesson.slides?.length || 0;
  const isFullyCompleted = answeredCount === totalSlides;

  const requiresApproval = config.features.requiresApproval;
  // `lessonAnswer` nu e populat de hook-ul curent (ramane mereu `undefined`) — sursa
  // corecta e `lesson.attemptState`, deja folosit de `isPracticeMode` in LessonPlayer.
  // ⚠️ INERT pe calea de azi: `GET /api/lessons/{id}` (LessonService.findById,
  // backend/.../service/LessonService.java:64-69) nu seteaza `attemptState` — doar
  // listarea (`findByType`, acelasi fisier, linia 155) o face. Pana la fixul de
  // backend, `state` ramane `undefined` aici la fel cum era prin `lessonAnswer`,
  // deci comportamentul pe DEV/PROD nu se schimba (nu regreseaza) — dar codul
  // devine corect din prima zi in care backend-ul populeaza campul.
  const state = lesson.attemptState as LessonAttemptState | undefined;
  const isApproved = state === LessonAttemptState.APPROVED;
  const isRejected = state === LessonAttemptState.REJECTED;
  const isPending = state === LessonAttemptState.PENDING_APPROVAL;

  return (
    <Card className="w-full max-w-2xl border-0 shadow-xl overflow-hidden">
      {/* Success Header - Conditional for practice mode */}
      <CardHeader className={isPracticeMode
        ? "bg-gradient-to-r from-lilac to-magenta text-white text-center py-2"
        : "bg-gradient-to-r from-magenta to-success text-white text-center py-2"
      }>
        <div className="flex justify-center mb-1">
          <Avatar className="h-16 w-16 sm:h-20 sm:w-20">
            <AvatarFallback className="bg-white/20">
              {isPracticeMode ? (
                <RefreshCw className="h-8 w-8 text-white" />
              ) : (
                <CheckCircle className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
              )}
            </AvatarFallback>
          </Avatar>
        </div>
        <CardTitle className="text-fluid-2xl text-white mb-1">
          {isPracticeMode ? 'Exersare completată!' : 'Felicitări!'}
        </CardTitle>
        <p className="text-white/90 text-fluid-base">
          {isPracticeMode
            ? 'Ai parcurs lecția în modul de exersare'
            : (config.features.requiresApproval
                /* 🔴 SP-05a: pentru activitatea practica, textul nu mai numeste cine aproba.
               Masurat de B1: ruta permite DBEST, ADMIN si PROFESOR (profesorul limitat la
               scoala lui), deci «profesorului coordonator» nu era fals, dar nici complet — si
               NU se poate afla cine a aprobat efectiv: `lesson_answers` are `approved_at` si
               `is_approved`, dar nicio coloana „aprobat de". Formularea fara rol e singura
               mereu adevarata. Sub acelasi predicat ca restul SP-05a: cu flagul oprit, textul
               ramane exact cel de azi, pentru toate tipurile. */
            ? (esteActivitatePracticaCuClase(lesson.type)
                ? 'Trimis spre aprobare'
                : `Ai completat ${config.displayName} cu succes. Așteptăm aprobarea profesorului coordonator pentru acordarea punctelor`)
                : 'Ai completat lecția cu succes'
              )
          }
        </p>
      </CardHeader>

      {/* Main Content */}
      <CardContent className="space-y-1 pt-2">
        {/* 🔴 AVERTIZAREA STA AICI, nu pe ecranul de clase — si asta e o REPARATIE,
            nu o preferinta. Pe `EcranClase` era nerandabila prin constructie: lista
            intra in state la trimitere, iar acelasi handler face `COMPLETE_LESSON`,
            care duce `LessonPlayer` pe return-ul timpuriu cu ecranul asta —
            deasupra ramurii care monteaza ecranul de clase. Prop pasat corect,
            ramura corecta, alta ramura castiga.
            ⚠️ Aici scria „React grupeaza cele doua `setState`". NU s-a masurat, si
            nici nu e portant: cu grupare, ecranul de clase nu se monteaza niciodata
            cu lista nevida; fara, s-ar monta un singur cadru, nelizibil oricum.
            Faptul verificabil e ordinea ramurilor, si se citeste din sursa.
            Aici e si locul onest: «Trimis spre aprobare» si „aceste clase erau deja
            punctate" sunt acelasi moment, nu doua ecrane.
            ⚠️ Garda de reintrare e in `avertizareClaseDejaPunctate`, nu aici: ecranul
            asta se randeaza si la redeschiderea unei lectii completate. */}
        {avertizareClase.length > 0 && (
          <div
            role="status"
            /* SP-05a, valul (d), R2. ⚠️ `role="status"` are DEJA `aria-atomic="true"` implicit in
               specificatia ARIA, deci scris explicit nu repara nimic pe hartie — il scriu pentru
               ca implementarile de cititoare difera si pentru ca intentia sa fie citibila din cod,
               nu dedusa dintr-un tabel de valori implicite. Ce repara efectiv concurenta e
               cealalta jumatate: toastul de dupa 201 nu mai e `alert` asertiv. */
            aria-atomic="true"
            data-clase-deja-punctate
            className="rounded-md border border-warning bg-warning/10 p-3 text-sm text-dark"
          >
            {textAvertizareClase(avertizareClase)}
          </div>
        )}

        {/* Lesson Info */}
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-1">
            {lesson.title}
          </h2>
          <Badge variant="secondary">{config.displayName}</Badge>
        </div>

        <Separator />

        {/* Statistics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div className="stat-card-primary">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-lilac/30 rounded-lg shrink-0">
                <CheckCircle className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-primary font-medium">Progres</p>
                <p className="text-lg font-bold text-foreground tabular-nums">
                  {answeredCount} / {totalSlides}
                </p>
              </div>
            </div>
          </div>

          {/* 🔴 „Rezultat final" DISPARE la activitatea practica sub SP-05a, si nu e cosmetic:
              se contrazicea cu randul de deasupra („asteptam aprobarea" + un rezultat final),
              iar cu punctarea pe clase pornita in PROD cifra reala e `50 x clase`, stabilita
              abia la aprobare. Ecranul afirma un numar pe care aprobarea il contrazice.
              ⚠️ Conditionat pe acelasi predicat: cu flagul oprit, si pentru celelalte tipuri,
              cardul ramane exact cum e azi. Cifra „50" a celorlalte tipuri in asteptare nu e
              in raza SP-05a (decizie S0) — nu se atinge. */}
          {config.features.awardsPoints && !isPracticeMode
            && !esteActivitatePracticaCuClase(lesson.type) && (
            <div className="stat-card-warning">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-lilac/30 rounded-lg shrink-0">
                  <Award className="h-5 w-5 text-magenta" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-magenta font-medium">Rezultat final</p>
                  <p className="text-lg font-bold text-foreground tabular-nums">
                    {isPending ? (
                      <>0 <span className="text-sm text-muted-foreground">(în așteptare)</span></>
                    ) : (
                      totalPoints
                    )}
                    {lesson.punctajMaxim != null && ` / ${Number(lesson.punctajMaxim)}`}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Completion Time */}
        {lessonAnswer?.timestamp && (
          <>
            <Separator />
            <div className="flex items-center gap-3 p-1 bg-muted rounded-lg">
              <Clock className="h-5 w-5 text-muted-foreground shrink-0" />
              <div>
                <p className="text-xs text-muted-foreground">Completat la</p>
                <p className="text-sm font-medium text-foreground">
                  {formatUnixTimestamp(lessonAnswer.timestamp) ?? '-'}
                </p>
              </div>
            </div>
          </>
        )}

        {/* Approval Status */}
        {requiresApproval && (
          <>
            <Separator />
            <div className="space-y-1">
              {isPending && (
                <Alert className="border-border bg-muted">
                  <AlertCircle className="h-4 w-4 text-foreground" />
                  <AlertTitle className="text-foreground">
                    În așteptare
                  </AlertTitle>
                  <AlertDescription className="text-foreground">
                    Răspunsurile tale vor fi evaluate de un profesor. Vei primi o notificare când vor fi verificate.
                  </AlertDescription>
                </Alert>
              )}

              {isApproved && (
                <Alert className="border-success/30 bg-success/10">
                  <CheckCircle className="h-4 w-4 text-success" />
                  <AlertTitle className="text-dark">
                    Aprobat
                  </AlertTitle>
                  <AlertDescription className="text-dark">
                    Răspunsurile tale au fost aprobate de profesor!
                  </AlertDescription>
                </Alert>
              )}

              {isRejected && (
                <Alert className="border-destructive/30 bg-destructive/10">
                  <AlertCircle className="h-4 w-4 text-destructive" />
                  <AlertTitle className="text-destructive">
                    Respins
                  </AlertTitle>
                  <AlertDescription className="text-destructive/90">
                    {lessonAnswer?.rejectReason && (
                      <p className="mb-2">Motiv: {lessonAnswer.rejectReason}</p>
                    )}
                    {config.features.allowsMultipleAttempts && (
                      <p>Poți încerca din nou lecția.</p>
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </>
        )}

        {/* Message - Conditional for practice mode */}
        <div className={isPracticeMode
          ? "text-center py-1 px-1 bg-lilac/30 border border-lilac rounded-lg"
          : "text-center py-1 px-1 bg-muted/50 rounded-lg"
        }>
          <p className="text-foreground font-medium">
            {isPracticeMode
              ? 'Răspunsurile tale nu au fost salvate. Lecția rămâne marcată ca finalizată.'
              : (isFullyCompleted
                  ? 'Ai răspuns la toate slide-urile! Bine lucrat!'
                  : 'Ai răspuns la câteva slide-uri. Poți continua oricând.')
            }
          </p>
        </div>

        {/* Multiple Attempts Info - Hide in practice mode */}
        {config.features.allowsMultipleAttempts && !isPracticeMode && (
          <p className="text-xs text-center text-muted-foreground italic">
            Poți relua această lecție oricând pentru a-ți îmbunătăți scorul
          </p>
        )}
      </CardContent>

      {/* Actions */}
      <CardFooter className="border-t gap-3">
        <Button
          onClick={() => navigate(getUserHomePage(userInfo?.role))}
          variant="outline"
          className="flex-1 gap-2"
          size="lg"
        >
          <Home className="h-4 w-4" />
          Acasă
        </Button>

        <Button
          onClick={() => navigate(lessonsListingTarget)}
          className="flex-1 gap-2"
          size="lg"
        >
          Vezi toate lecțiile
        </Button>
      </CardFooter>
    </Card>
  );
};

export default CompletionScreen;

